/**
 * Parses docs/help (ALN-53). Pure: takes file text, returns articles and tips, so the app, the unit
 * tests and the content check (TC-HELP-005) all read the help the same way.
 *
 * An article is frontmatter (id, title, category, optional route), a `# Title`, a lead paragraph,
 * the body, then optional `## Related` (links to other articles) and `## Official sources`
 * (external links). tips.md is a table: Label | Tip | [Article](id.md).
 */
export type Block =
  | { t: "p"; text: string }
  | { t: "h"; level: 2 | 3; text: string }
  | { t: "ul" | "ol"; items: string[] }
  | { t: "table"; head: string[]; rows: string[][] };

export interface HelpArticle {
  id: string;
  title: string;
  category: string;
  /** The app route (without the slash) whose page help is this article. */
  route?: string;
  lead: string;
  body: Block[];
  related: string[];
  sources: { label: string; href: string }[];
}

export interface Tip { label: string; text: string; article: string }

/** Category keys in frontmatter, in help center order, with their headings. */
export const CATEGORIES: [string, string][] = [
  ["start", "Getting started"],
  ["screens", "Screens"],
  ["concepts", "How costing works"],
  ["rules", "Compliance rules"],
  ["integrations", "Integrations"],
];

const lines = (s: string) => s.replace(/\r\n?/g, "\n").split("\n");
const LINK = /^\[([^\]]+)\]\(([^)]+)\)$/;
const articleId = (href: string) => href.replace(/^\.\//, "").replace(/\.md$/, "");

function frontmatter(src: string[]): { meta: Record<string, string>; rest: string[] } {
  if (src[0]?.trim() !== "---") return { meta: {}, rest: src };
  const end = src.indexOf("---", 1);
  const meta: Record<string, string> = {};
  for (const l of src.slice(1, end)) {
    const m = /^(\w+):\s*(.*)$/.exec(l.trim());
    if (m) meta[m[1]!] = m[2]!.replace(/^"(.*)"$/, "$1");
  }
  return { meta, rest: src.slice(end + 1) };
}

const cells = (l: string) => l.trim().replace(/^\|/, "").replace(/\|$/, "").split("|").map(c => c.trim());

/** Markdown blocks: headings, paragraphs, bullet and numbered lists, tables. */
export function parseBlocks(src: string[]): Block[] {
  const out: Block[] = [];
  let para: string[] = [];
  const flush = () => { if (para.length) out.push({ t: "p", text: para.join(" ") }); para = []; };
  for (let i = 0; i < src.length; i++) {
    const l = src[i]!.trimEnd();
    if (!l.trim()) { flush(); continue; }
    const h = /^(#{2,3})\s+(.*)$/.exec(l);
    if (h) { flush(); out.push({ t: "h", level: h[1]!.length as 2 | 3, text: h[2]! }); continue; }
    const li = /^(?:([-*])|(\d+)\.)\s+(.*)$/.exec(l);
    if (li) {
      flush();
      const kind = li[1] ? "ul" : "ol";
      const last = out[out.length - 1];
      if (last && last.t === kind) last.items.push(li[3]!);
      else out.push({ t: kind, items: [li[3]!] });
      // a following non-blank, non-list line continues this item
      continue;
    }
    if (l.startsWith("|")) {
      flush();
      const rows: string[][] = [];
      for (; i < src.length && src[i]!.trim().startsWith("|"); i++) rows.push(cells(src[i]!));
      i--;
      const [head, , ...body] = rows;
      out.push({ t: "table", head: head ?? [], rows: body.filter(r => !r.every(c => /^:?-+:?$/.test(c))) });
      continue;
    }
    const last = out[out.length - 1];
    if (!para.length && last && (last.t === "ul" || last.t === "ol") && src[i - 1]?.trim()) {
      last.items[last.items.length - 1] += ` ${l.trim()}`;
      continue;
    }
    para.push(l.trim());
  }
  flush();
  return out;
}

export function parseArticle(file: string, text: string): HelpArticle {
  const { meta, rest } = frontmatter(lines(text));
  const id = meta.id || file.replace(/\.md$/, "");
  const blocks = parseBlocks(rest);
  // drop the "# Title" line (parseBlocks reads only ## and ###, so it arrives as a paragraph)
  if (blocks[0]?.t === "p" && blocks[0].text.startsWith("# ")) blocks.shift();
  const lead = blocks[0]?.t === "p" ? (blocks.shift() as { text: string }).text : "";
  const body: Block[] = [];
  const related: string[] = [];
  const sources: { label: string; href: string }[] = [];
  let section: "body" | "related" | "sources" = "body";
  for (const b of blocks) {
    if (b.t === "h" && b.level === 2 && /^related$/i.test(b.text)) { section = "related"; continue; }
    if (b.t === "h" && b.level === 2 && /^official sources$/i.test(b.text)) { section = "sources"; continue; }
    if (b.t === "h" && b.level === 2) section = "body";
    if (section === "body") { body.push(b); continue; }
    if (b.t !== "ul") continue;
    for (const item of b.items) {
      const m = LINK.exec(item.trim());
      if (!m) continue;
      if (section === "related") related.push(articleId(m[2]!));
      else sources.push({ label: m[1]!, href: m[2]! });
    }
  }
  return { id, title: meta.title || id, category: meta.category || "start", route: meta.route || undefined, lead, body, related, sources };
}

export function parseTips(text: string): Tip[] {
  const out: Tip[] = [];
  for (const l of lines(text)) {
    if (!l.trim().startsWith("|")) continue;
    const [label, tip, link] = cells(l);
    if (!label || !tip || label === "Label" || /^:?-+:?$/.test(label)) continue;
    const m = LINK.exec(link ?? "");
    out.push({ label, text: tip, article: m ? articleId(m[2]!) : "" });
  }
  return out;
}

/** Article ids in the order README.md lists them (the help center's order within a category). */
export function parseIndexOrder(readme: string): string[] {
  const ids: string[] = [];
  for (const m of readme.matchAll(/^- \[[^\]]+\]\(([^)]+\.md)\)/gm)) ids.push(articleId(m[1]!));
  return ids;
}

/** Inline markdown: **bold**, *em*, `code` and [links](href). */
export type Inline = { t: "text" | "b" | "em" | "code"; text: string } | { t: "a"; text: string; href: string };
export function parseInline(s: string): Inline[] {
  const out: Inline[] = [];
  const re = /\*\*([^*]+)\*\*|`([^`]+)`|\[([^\]]+)\]\(([^)]+)\)|\*([^*\s][^*]*)\*/g;
  let at = 0;
  for (const m of s.matchAll(re)) {
    if (m.index! > at) out.push({ t: "text", text: s.slice(at, m.index) });
    if (m[1] != null) out.push({ t: "b", text: m[1] });
    else if (m[2] != null) out.push({ t: "code", text: m[2] });
    else if (m[3] != null) out.push({ t: "a", text: m[3], href: m[4]! });
    else out.push({ t: "em", text: m[5]! });
    at = m.index! + m[0].length;
  }
  if (at < s.length) out.push({ t: "text", text: s.slice(at) });
  return out;
}

/** Text for searching an article: title, lead and body, without markdown. */
export function plainText(a: HelpArticle): string {
  const blockText = (b: Block) => b.t === "p" || b.t === "h" ? b.text : b.t === "table" ? [...b.head, ...b.rows.flat()].join(" ") : b.items.join(" ");
  return [a.title, a.lead, ...a.body.map(blockText)].join(" ").replace(/\*\*|`|\[|\]\([^)]*\)/g, "");
}

export interface ContentProblem { file: string; problem: string }

/**
 * The content check (TC-HELP-005): every app route has an article, every tip and Related link
 * points at an article that exists, and README lists every article.
 */
export function checkHelpContent(input: { articles: HelpArticle[]; tips: Tip[]; routes: string[]; indexOrder: string[] }): ContentProblem[] {
  const problems: ContentProblem[] = [];
  const ids = new Set(input.articles.map(a => a.id));
  const byRoute = new Map(input.articles.filter(a => a.route).map(a => [a.route!, a.id]));
  for (const r of input.routes) if (!byRoute.has(r)) problems.push({ file: "docs/help", problem: `route /${r} has no article (add one with route: ${r})` });
  for (const t of input.tips) {
    if (!t.article) problems.push({ file: "tips.md", problem: `tip "${t.label}" has no article link` });
    else if (!ids.has(t.article)) problems.push({ file: "tips.md", problem: `tip "${t.label}" links to missing article ${t.article}.md` });
  }
  for (const a of input.articles) {
    for (const r of a.related) if (!ids.has(r)) problems.push({ file: `${a.id}.md`, problem: `Related links to missing article ${r}.md` });
    if (!input.indexOrder.includes(a.id)) problems.push({ file: "README.md", problem: `article ${a.id}.md isn't listed` });
    if (!CATEGORIES.some(([k]) => k === a.category)) problems.push({ file: `${a.id}.md`, problem: `unknown category ${a.category}` });
    if (!a.lead) problems.push({ file: `${a.id}.md`, problem: "no lead paragraph" });
    for (const s of a.sources) if (!/^https:\/\//.test(s.href)) problems.push({ file: `${a.id}.md`, problem: `official source ${s.label} isn't an https link` });
  }
  const labels = new Set<string>();
  for (const t of input.tips) { if (labels.has(t.label)) problems.push({ file: "tips.md", problem: `tip "${t.label}" is listed twice` }); labels.add(t.label); }
  return problems;
}
